import { useRootStackNavigation } from '$tablet-app/src/app/navigation/hooks';
import { palette } from '$tablet-app/src/app/theme';
import { Button } from '@akin-home/buttons/native';
import { Text } from '@akin-home/text/native';
import { spacing } from '@akin-home/theme';
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from '@akin-home/toast/native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import DeleteButton from '../../components/DeleteButton';
import { useRobotStateStore } from '../../stores/useRobotStateStore';
import { useDeleteRobotIdentity } from '../hooks/mutations';
import { useGetRobotIdentity } from '../hooks/queries';

interface RobotIdentityDetailsProps {
  robotId: string;
  identity_id: string | undefined;
  onClose?: () => any;
}

export const RobotIdentityDetails = (props: RobotIdentityDetailsProps) => {
  const { robotId, identity_id } = props;

  const { navigate } = useRootStackNavigation();

  const robot = useRobotStateStore((state) => state.robotDevices[robotId]);
  const { data: identity } = useGetRobotIdentity(robot, identity_id);

  const deleteMutation = useDeleteRobotIdentity(robot);

  const handleDelete = async () => {
    showInfoToast({ text1: 'Deleting Identity...' });
    try {
      if (!identity?.uuid || !identity_id) {
        showErrorToast({
          text1: 'Identity could not be found.',
        });
        return;
      }

      await deleteMutation.mutateAsync(identity_id);
      showSuccessToast({
        text1: 'Identity Deleted',
      });
      props.onClose?.();
    } catch (err) {
      console.error('Error when deleting identity:', err);
      showErrorToast({
        text1: 'Error in Deleting Identity',
        text2: 'Please try again.',
      });
    }
  };

  if (robot === undefined || identity === undefined) {
    return (
      <View style={styles.loadingContainer}>
        <Text variant="h3" weight="semibold" color={palette.dim}>
          Select an Identity to View Details
        </Text>
      </View>
    );
  }

  return (
    <>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.containerContent}
      >
        {/* User Photo */}
        <View style={styles.photoContainer}>
          <FontAwesomeIcon icon={'user'} color={'#c9c9c9'} size={60} />
        </View>

        {/* User Name */}
        <View style={styles.userNameContainer}>
          <Text variant="h2" weight="semibold">
            {identity.name ?? 'Unnamed'}
          </Text>
          {identity.is_primary_user ? (
            <View style={styles.starContainer}>
              <FontAwesomeIcon icon={'star'} color="gold" size={25} />
            </View>
          ) : null}
        </View>

        {/* User Nicknames */}
        <View style={[styles.detailsRowContainer, styles.rolesContainer]}>
          <Text variant="p" weight="semibold" style={styles.detailsTextHeader}>
            Nicknames:
          </Text>

          <View style={styles.rolesBorder}>
            <ScrollView
              horizontal
              contentContainerStyle={styles.rolesScrollViewContainer}
            >
              <Text>
                {identity?.nicknames?.map((elem) => elem.text).join(', ') ?? ''}
              </Text>
            </ScrollView>
          </View>
        </View>

        {/* Last Seen Timestamp */}
        <View style={styles.detailsRowContainer}>
          <Text variant="p" weight="semibold" style={styles.detailsTextHeader}>
            Last Seen:
          </Text>
          <Text variant="p" weight="regular" style={styles.detailsTextValue}>
            {convertTimestampToDateString(identity.time_last_seen) ?? ''}
          </Text>
        </View>

        {/* User Roles */}
        <View style={[styles.detailsRowContainer, styles.rolesContainer]}>
          <Text variant="p" weight="semibold" style={styles.detailsTextHeader}>
            Roles:
          </Text>

          <View style={styles.rolesBorder}>
            <ScrollView
              contentContainerStyle={styles.rolesScrollViewContainer}
              horizontal
            >
              {identity?.roles?.map((elem, index) => (
                <View key={`${elem}_${index}`} style={styles.rolesPill}>
                  <Text>{elem}</Text>
                </View>
              ))}
            </ScrollView>
          </View>
        </View>

        {/* User Notes */}
        <View style={styles.notesContainer}>
          <Text variant="p" weight="semibold" style={styles.detailsTextHeader}>
            Notes:
          </Text>

          <Text variant="p" weight="regular" style={styles.notesTextValue}>
            {identity?.notes}
          </Text>
        </View>
      </ScrollView>

      {/* Edit Button */}
      <View style={styles.editButtonContainer}>
        <Button
          size="large"
          variant="secondary"
          icon="edit"
          onPress={() =>
            navigate('EditRobotIdentity', {
              robotId,
              identity_id: identity.uuid,
            })
          }
          style={styles.editButton}
        >
          Edit Identity
        </Button>
      </View>

      <View style={styles.floatingButtonsContainer}>
        {/* Delete button */}
        <DeleteButton
          deleteModalProps={{
            title: 'Are you sure you want to delete this identity?',
            confirmDeleteString: `Delete ${identity.name ?? 'Unnamed'}`,
          }}
          onConfirm={handleDelete}
        />

        {/* Close button */}
        <Button
          icon="times"
          size="medium"
          iconSize={20}
          variant="secondary"
          onPress={() => props?.onClose?.()}
        />
      </View>
    </>
  );
};

const convertTimestampToDateString = (timestamp: number | undefined | null) => {
  if (timestamp === undefined || timestamp === null) {
    return undefined;
  }

  // A zero or negative timestamp is treated as an invalid value in our system
  if (timestamp <= 0) {
    return undefined;
  }

  return new Date(timestamp * 1000).toLocaleString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  });
};

const styles = StyleSheet.create({
  container: {
    gap: spacing[3],
    flex: 1,
    height: '100%',
  },
  containerContent: {
    gap: spacing[3],
  },
  loadingContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoContainer: {
    width: 175,
    height: 175,
    borderRadius: 175 / 2,
    backgroundColor: '#e9e9e9',
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[2],
  },
  userNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  starContainer: {
    marginLeft: spacing[3],
  },
  detailsRowContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    minHeight: 30,
  },
  rolesContainer: {
    flexBasis: '30%',
  },
  notesContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[3],
    minHeight: 30,
    flexBasis: '30%',
  },
  rolesBorder: {
    flex: 1,
    borderColor: 'black',
    borderWidth: 1,
    borderRadius: 10,
    padding: spacing[2],
  },
  rolesScrollViewContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
  },
  rolesPill: {
    backgroundColor: palette.lightGrey1,
    padding: spacing[1],
    borderRadius: 10,
  },
  detailsTextHeader: {
    width: '25%',
  },
  detailsTextValue: {
    flex: 1,
  },
  notesTextValue: {
    flex: 1,
    paddingBottom: spacing[9],
  },
  editButtonContainer: {
    position: 'absolute',
    bottom: 0.1,
    borderRadius: 20,
    // This is to center the button. unfortunately, this may reduce responsiveness. So consider replacing with an onlayout based approach.
    left: '35%',
    right: '35%',
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: 'white',
  },
  editButton: {
    flex: 1,
  },
  floatingButtonsContainer: {
    position: 'absolute',
    right: 0,
    top: 0,
    flexDirection: 'row',
    gap: spacing[2],
    justifyContent: 'flex-end',
  },
});
